
import { motion } from "framer-motion";
import { Heart, ChefHat, Camera, Music, Gift, Gem } from "lucide-react";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";

export const WeddingThemeSection = () => {
  const { toast } = useToast();
  const [hasShownToast, setHasShownToast] = useState(false);
  
  // Show toast after 10 seconds of reading
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasShownToast) {
        toast({
          title: "Hääbudjetista huolissasi?",
          description: "Saat kilpailukykyisen tarjouksen häiden rahoitukseen helposti.",
          action: (
            <Link 
              to="/kilpailuta" 
              className="bg-pink-600 text-white px-4 py-2 rounded-md hover:bg-pink-700 transition-colors"
            >
              Kilpailuta laina
            </Link>
          ),
        });
        setHasShownToast(true);
      }
    }, 10000);
    
    return () => clearTimeout(timer);
  }, [toast, hasShownToast]);

  const weddingExpenses = [
    { 
      icon: <Gem className="h-10 w-10 text-pink-500" />,
      title: "Kihla- ja vihkisormukset", 
      description: "Täydellinen sormus symboloi rakkauttanne",
      articlePath: "/artikkelit/lainavertailu" 
    },
    { 
      icon: <Camera className="h-10 w-10 text-pink-500" />,
      title: "Valokuvaus ja videokuvaus", 
      description: "Ikuista elämäsi tärkein päivä",
      articlePath: "/artikkelit/kulutusluotto-vertailu" 
    },
    { 
      icon: <ChefHat className="h-10 w-10 text-pink-500" />,
      title: "Hääjuhlan catering", 
      description: "Herkullinen menu kaikille vieraille",
      articlePath: "/artikkelit/yhdistelylaina-opas" 
    },
    { 
      icon: <Music className="h-10 w-10 text-pink-500" />,
      title: "Musiikki ja viihde", 
      description: "Tunnelmallista musiikkia juhlaan",
      articlePath: "/artikkelit/asuntolaina-opas" 
    },
    { 
      icon: <Gift className="h-10 w-10 text-pink-500" />,
      title: "Häälahjat ja kiitoslahjat", 
      description: "Muista vieraitasi tyylikkäästi",
      articlePath: "/artikkelit/korkojen-nousu" 
    },
    { 
      icon: <Heart className="h-10 w-10 text-pink-500" />,
      title: "Häiden koristelu", 
      description: "Luo täydellinen tunnelma juhlapaikalle",
      articlePath: "/artikkelit/energiakriisi" 
    }
  ];

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      custom={5}
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: (i: number) => ({
          opacity: 1,
          y: 0,
          transition: {
            delay: i * 0.1,
            duration: 0.5,
          }
        })
      }}
      className="mb-12"
    >
      <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-8 border border-pink-100">
        <h2 className="text-2xl font-bold text-pink-700 mb-6 flex items-center">
          <Heart className="mr-2 text-pink-500" />
          Täydellisten häiden rahoitus
        </h2>
        
        <p className="text-gray-700 mb-6">
          Unelmiesi häät ansaitsevat kaiken sen loiston, mitä olet aina kuvitellut. Remonttilaina 
          on joustava rahoitusvaihtoehto, joka mahdollistaa häidesi järjestämisen juuri sellaiseksi 
          kuin olet ne kuvitellut - ilman kompromisseja.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {weddingExpenses.map((item, index) => (
            <Link 
              key={index} 
              to={item.articlePath}
              className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow flex flex-col items-center text-center"
              onClick={() => {
                // Show toast after clicking an item if not shown before
                if (!hasShownToast) {
                  setTimeout(() => {
                    toast({
                      title: "Häiden rahoitus",
                      description: "Rahoita unelmiesi häät kilpailukykyisellä lainalla.",
                      action: (
                        <Link 
                          to="/kilpailuta" 
                          className="bg-pink-600 text-white px-4 py-2 rounded-md hover:bg-pink-700 transition-colors"
                        >
                          Hae lainaa
                        </Link>
                      ),
                    });
                  }, 5000);
                  setHasShownToast(true);
                }
              }}
            >
              <div className="mb-3">
                {item.icon}
              </div>
              <h3 className="font-semibold text-pink-700 mb-1">{item.title}</h3>
              <p className="text-sm text-gray-600">{item.description}</p>
            </Link>
          ))}
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-md border border-pink-100">
          <h3 className="text-lg font-semibold text-pink-700 mb-3">Miksi valita remonttilaina häiden rahoitukseen?</h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
            <li>Joustava lainamäärä häiden budjetin mukaan</li>
            <li>Kohtuullinen korko verrattuna luottokorttiin</li>
            <li>Selkeä maksusuunnitelma, joka sopii talouteesi</li>
            <li>Nopea päätös - saat rahoituksen suunnittelun alkuvaiheessa</li>
            <li>Ei ylimääräisiä kuluja tai piilokuluja</li>
          </ul>
          
          <Link 
            to="/kilpailuta" 
            className="inline-flex items-center bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white px-6 py-3 rounded-md font-medium transition-colors"
          >
            Hae rahoitusta unelmiesi häihin
            <Heart className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};
